import { useMemo, useState } from 'react';
import { useParticipantEPR } from '../ParticipantEPRContext';
import { bloodGroups } from '../../facilitator-components/scenariobuilder/bloodConfig';
import { useSession } from '../SessionContext';
import {
  Box, Paper, Typography, Button, Modal, Chip,
  Stack, Table, TableHead, TableRow, TableCell, TableBody,
  Autocomplete, TextField
} from '@mui/material';
import dayjs from 'dayjs';

const ACCENT_BORDER = '#60A5FA';          // match NEWS table frame
const TOPBAR_BG = '#dbeafe';              // match NEWS table header
const HEAD_BG = '#F3F4F6';

const TEST_DISPLAY_ORDER_BY_LABEL = {
  'Full Blood Count': ['WCC','Hb','Haematocrit','MCV','Platelets','Neutrophils','Lymphocytes','Monocytes','Eosinophils','Basophils'],
  'Urea & Electrolytes': ['Sodium','Potassium','Urea','Creatinine','eGFR'],
  'Liver Function Tests': ['Albumin','ALT','ALP','AST','Bilirubin'],
  'CRP': ['CRP'],
  'Clotting': ['PT','INR','APTT','Fibrinogen'],
  'ABG': ['pH','pCO2','pO2','HCO3','Base','K+','Na+','Ca2+','Cl-','Glu','Lac','Hb','sO2'],
  'Group & Save': ['Group','Antibody screen']
};

const isAbnormal = (val, low, high) => {
  const num = parseFloat(val), lo = parseFloat(low), hi = parseFloat(high);
  if (Number.isFinite(num) && Number.isFinite(lo) && Number.isFinite(hi)) return num < lo || num > hi;
  if (typeof val === 'string' && typeof low === 'string' && typeof high === 'string') return val !== low && val !== high;
  return false;
};

export default function BloodResults() {
  const { eprData } = useParticipantEPR();
  const API_BASE = process.env.REACT_APP_API_URL;
  const bloodSets = eprData?.eprBloods ?? [];
  const { socket, room, liveId } = useSession();
  const [savingReq, setSavingReq] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestGroups, setRequestGroups] = useState([]);

  // Canonical group order from config (labels)
  const GROUP_ORDER = useMemo(() => Object.values(bloodGroups).map(g => g.label), []);

  // Config element order per label (fallback for row order)
  const CONFIG_LABEL_TO_ORDER = useMemo(() => {
    const m = {};
    for (const g of Object.values(bloodGroups)) m[g.label] = g.elements.map(e => e.name);
    return m;
  }, []);

  // Normalize: ts -> groupLabel -> [tests]
  const { bloodData, datetimes, presentGroupLabels } = useMemo(() => {
    const map = {};
    const setLabels = new Set();
    for (const set of bloodSets) {
      const ts = set.timestamp;
      if (!map[ts]) map[ts] = {};
      for (const group of set.groups || []) {
        map[ts][group.label] = group.values || [];
        setLabels.add(group.label);
      }
    }
    const dts = Object.keys(map).sort(); // newest first
    return { bloodData: map, datetimes: dts, presentGroupLabels: setLabels };
  }, [bloodSets]);

  // Render groups in config order, then unknowns
  const groupsToRender = useMemo(() => {
    const known = GROUP_ORDER.filter(lbl => presentGroupLabels.has(lbl));
    const unknown = [...presentGroupLabels].filter(lbl => !GROUP_ORDER.includes(lbl)).sort();
    return [...known, ...unknown];
  }, [GROUP_ORDER, presentGroupLabels]);

  // Ensure min 5 columns
  const paddedHeader = useMemo(() => {
    const pads = Math.max(0, 5 - datetimes.length);
    return [...datetimes, ...Array(pads).fill(null)];
  }, [datetimes]);

  const handleSendRequest = async() => {
    if (!liveId || !room || !requestGroups.length) return;

    // Convert selected group KEYS -> human labels (strings)
    const labels = requestGroups.map(k => bloodGroups[k]?.label || k);

    // Store as JSON string in a TEXT column (keeps structure)
    // If you prefer CSV, use: labels.join(', ')
    const requestmsg = JSON.stringify(labels);
    try {
      setSavingReq(true)
      await fetch(`${API_BASE}/api/requestbloods`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
          livescenarioid: liveId,
          room,
          requestmsg: requestmsg,
          }),
      });
    } catch (err) {
      console.error('Error requesting bloods:', err);
      alert('Failed to request bloods. Please try again.');
    } finally {
      setSavingReq(false);
    };

    // TODO: call your API/socket here with requestGroups
    setRequestOpen(false);
    setRequestGroups([]);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${ACCENT_BORDER}`,
        borderRadius: 3,
        bgcolor: 'white',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0   // IMPORTANT for flex children to be allowed to shrink
      }}
    >
      {/* Top bar — same height/spacing as NEWS table */}
      <Box sx={{
        px: 2, py: 1,
        backgroundColor: TOPBAR_BG,
        borderBottom: `1px solid ${ACCENT_BORDER}`, borderTopRightRadius:'30px', borderTopLeftRadius:'30px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <Box sx={{ fontWeight: 750, fontSize: '20px' }}>Blood results</Box>
        <Button variant="contained" size="small" onClick={() => setRequestOpen(true)} sx={{ textTransform: 'none', boxShadow: 'none' }}>
          Request
        </Button>
      </Box>

      <Box
        sx={{
          p: 2,
          overflow: 'auto',        // vertical + horizontal scroll
          minHeight: 0,            // flex child can shrink within parent
          maxHeight: 'calc(100vh - 180px)' // adjust if your header area is taller/shorter
        }}
      >
        <Table
          size="small"
          sx={{
            tableLayout: 'fixed',
            borderCollapse: 'collapse',
            width: '100%',
            border: '1px solid #E5E7EB',
            '& th, & td': { borderRight: '1px solid #E5E7EB' },
            '& th:last-child, & td:last-child': { borderRight: 'none' }
          }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: HEAD_BG }}>
              <TableCell sx={{ fontWeight: 700, width: 260 }}></TableCell>
              {paddedHeader.map((dt, i) => (
                <TableCell key={i} align="center" sx={{ fontWeight: 700 }}>
                  {dt ? dayjs(dt).format('DD/MM/YYYY HH:mm') : ''}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {groupsToRender.map((groupLabel) => {
              // gather seen tests under this label
              const seen = new Set();
              for (const dt of datetimes) (bloodData[dt]?.[groupLabel] || []).forEach(t => seen.add(t.name));
              if (seen.size === 0) return null;

              const preferred =
                TEST_DISPLAY_ORDER_BY_LABEL[groupLabel] ||
                CONFIG_LABEL_TO_ORDER[groupLabel] ||
                Array.from(seen);

              const rowOrder = preferred.filter(n => seen.has(n));

              return (
                <Box component="tbody" key={groupLabel} sx={{ display: 'contents' }}>
                  <TableRow>
                    <TableCell colSpan={1 + paddedHeader.length} sx={{
                      bgcolor: '#EFF6FF',
                      fontWeight: 700,
                      borderTop: '2px solid #1F2937',
                      borderBottom: '1px solid #CBD5E1'
                    }}>
                      {groupLabel}
                    </TableCell>
                  </TableRow>

                  {rowOrder.map((testName) => (
                    <TableRow key={`${groupLabel}-${testName}`}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Stack direction="row" spacing={1} alignItems="baseline">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{testName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(() => {
                              for (const dt of datetimes) {
                                const t = (bloodData[dt]?.[groupLabel] || []).find(x => x.name === testName);
                                if (t?.units) return t.units;
                              }
                              return '';
                            })()}
                          </Typography>
                        </Stack>
                      </TableCell>

                      {paddedHeader.map((dt, i) => {
                        if (!dt) return <TableCell key={i} />;
                        const t = (bloodData[dt]?.[groupLabel] || []).find(x => x.name === testName);
                        const abnormal = t && isAbnormal(t.value, t.refLow, t.refHigh);
                        return (
                          <TableCell
                            key={i}
                            align="center"
                            sx={{
                              bgcolor: abnormal ? 'rgba(239, 68, 68, 0.12)' : 'transparent',
                              fontWeight: abnormal ? 700 : 400,
                              color: abnormal? 'red' : 'black'
                            }}
                          >
                            {t ? String(t.value) : ''}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </Box>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {/* Request modal (same visual language as other pages) */}
      <Modal open={requestOpen} onClose={() => setRequestOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 700, maxWidth: '95vw', bgcolor: 'white', borderRadius: 3,
          border: `1px solid ${ACCENT_BORDER}`, boxShadow: 24, overflow: 'hidden'
        }}>
          <Box sx={{
            px: 2, py: 1,
            backgroundColor: TOPBAR_BG,
            borderBottom: `1px solid ${ACCENT_BORDER}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <Typography sx={{ fontWeight: 800 }}>Request blood tests</Typography>
            <Chip size="small" label="Participant" />
          </Box>

          <Box sx={{ p: 2 }}>
            <Autocomplete
              multiple
              options={Object.keys(bloodGroups)}
              value={requestGroups}
              onChange={(e, v) => setRequestGroups(v)}
              getOptionLabel={(key) => bloodGroups[key]?.label || key}
              renderInput={(params) => (
                <TextField {...params} label="Select blood groups" placeholder="Search…" />
              )}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, px: 2, pb: 2 }}>
            <Button onClick={() => setRequestOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSendRequest} disabled={(requestGroups?.length ?? 0) === 0 || savingReq}>
              Send request
            </Button>
          </Box>
        </Box>
      </Modal>
    </Paper>
  );
}
